import { useForm, Controller } from 'react-hook-form';
import { useAppSelector } from 'store/index';
import { selectCompany, selectCompanyId } from 'store/selectors/me/company';
import { SettingsDescription, SettingsMain, SettingsPageHeader } from '../SettingsLayout';
import CommerceTypeAvatar from 'storybookConfig/stories/cells/Avatar/CommerceTypeAvatar';
import { CommerceType, FULFILLMENT_TIME_OPTIONS } from 'types/models/company';
import Form from 'storybookConfig/stories/organisms/Form';
import Input from 'storybookConfig/stories/molecules/Input';
import Select from 'storybookConfig/stories/molecules/Select';
import RadioButton from 'storybookConfig/stories/molecules/RadioButton';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import StatusIcon from 'storybookConfig/stories/molecules/Icon/StatusIcon';
import Divider from 'storybookConfig/stories/molecules/Divider';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import useAlertQueue from 'hooks/useAlertQueue';
import type { Company } from 'types/models/company';
import RevenueSliderStandalone from 'containers/MarketplaceOnboardingPage/RevenueSliderStandalone';
import UnitInput from 'storybookConfig/stories/molecules/Input/UnitInput';
import Icon from 'storybookConfig/stories/molecules/Icon';
import Label from 'storybookConfig/stories/molecules/Label';
import styled from '@emotion/styled';

import revSplitIcon from '../../../images/rev-split-icon.svg';
import { useState } from 'react';
import RevenueSplitPanel from 'components/Common/RevenueSplitPanel';
import RetailerAccessPanel from 'components/Common/RetailerAccessPanel';
import FulfillmentTimesPanel from 'components/Common/FulfillmentTimesPanel';
import ReturnPolicyPanel from 'components/Common/ReturnPolicyPanel';
import lockCheckIcon from '../../../images/lock-check.svg';
import deliveryTruckIcon from '../../../images/delivery-truck.svg';
import returnPolicyIcon from '../../../images/return-policy-icon.svg';
import RetailerAccessSlider from 'containers/MarketplaceOnboardingPage/RetailerAccessSlider';
import { useDispatch } from 'react-redux';
import { getCurrentUserAndCompany } from 'store/slices/me';
import { ThemeColors } from 'storybookConfig/stories/theme';

interface FormFields {
  revSplit?: number;
  retailerAccess?: string;
  approvalThreshold?: number;
  fulfillmentTimes?: string;
  returnPolicyURL?: string;
  returnPolicyOption?: 'hasPolicy' | 'noPolicy';
}

const InfoIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.color.surfaceSubtle};
  }
`;

const CustomModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${ThemeColors.scrim};
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CustomModalContent = styled.div`
  position: relative;
  z-index: 2001;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CustomModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: ${ThemeColors.bodyTextCaption};
  cursor: pointer;
  z-index: 2002;
`;

const RevenueSplitField = ({
  value,
  onChange,
  isRequired,
  onInfoClick,
  inputValue,
  onInputChange,
  inputError,
}: {
  value?: number;
  onChange: (value: number) => void;
  isRequired?: boolean;
  onInfoClick: () => void;
  inputValue?: number;
  onInputChange: (value: number) => void;
  inputError?: string;
}) => {
  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    if (!Number.isNaN(val)) {
      const clamped = Math.min(Math.max(val, 0), 100);
      onInputChange(clamped);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <Label htmlFor="revSplit" isRequired={isRequired}>
          Revenue split
        </Label>
        <InfoIconButton
          type="button"
          onClick={onInfoClick}
          aria-label="Learn more about revenue split"
        >
          <Icon name="info" size="16px" color="bodyTextCaption" />
        </InfoIconButton>
      </div>
      <div>
        <RevenueSliderStandalone
          value={value}
          onChange={onChange}
          min={0}
          max={100}
          step={1}
          width="100%"
        />
      </div>
      <Form.Field labelText="Revenue Split" id="revSplitInput" errorText={inputError} isRequired>
        <UnitInput
          value={inputValue || 0}
          onChange={handleNumberInputChange}
          min={0}
          max={100}
          unit="%"
          step={1}
          isFullWidth
        />
      </Form.Field>
    </div>
  );
};

const ServiceOptions = () => {
  const company = useAppSelector(selectCompany);
  const companyId = useAppSelector(selectCompanyId);
  const { commerceType } = company ?? {};
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const [showRevenueSplitModal, setShowRevenueSplitModal] = useState(false);
  const [showRetailerAccessModal, setShowRetailerAccessModal] = useState(false);
  const [showFulfillmentTimesModal, setShowFulfillmentTimesModal] = useState(false);
  const [showReturnPolicyModal, setShowReturnPolicyModal] = useState(false);
  const dispatch = useDispatch();

  const { handleSubmit, register, watch, control, formState } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      revSplit: company?.marketplaceSettings?.revSplit,
      retailerAccess: company?.marketplaceSettings?.retailerAccess,
      approvalThreshold: company?.marketplaceSettings?.approvalThreshold,
      fulfillmentTimes: company?.marketplaceSettings?.fulfillmentTimes,
      returnPolicyURL: company?.marketplaceSettings?.returnPolicyURL,
      returnPolicyOption: company?.marketplaceSettings?.returnPolicyURL ? 'hasPolicy' : 'noPolicy',
    },
  });

  const selectedOption = watch('returnPolicyOption');

  const { mutate: updateServiceOptions, isLoading } = useMutation<Company, Error, FormFields>({
    mutationFn: (data: FormFields) => {
      const updatedData = {
        revSplit: Number(data.revSplit),
        retailerAccess: data.retailerAccess,
        approvalThreshold:
          data.retailerAccess === 'HYBRID' ? Number(data.approvalThreshold) : undefined,
        fulfillmentTimes: data.fulfillmentTimes,
        returnPolicyURL: data.returnPolicyOption === 'hasPolicy' ? data.returnPolicyURL : '',
      };

      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: () => {
      addSuccessAlert(
        'Service Options Updated',
        'Your service options have been saved successfully.'
      );
      dispatch(getCurrentUserAndCompany() as any);
    },
    onError: (error) => {
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update service options', error);
    },
  });

  const onSubmit = (data: FormFields) => {
    updateServiceOptions(data);
  };

  return (
    <>
      <SettingsMain>
        <SettingsPageHeader title="Service Options">
          <CommerceTypeAvatar commerceType={commerceType || CommerceType.None} />
        </SettingsPageHeader>
        <SettingsDescription>
          Configure your service options and policies for the marketplace.
        </SettingsDescription>
        <Form onSubmit={handleSubmit(onSubmit)} style={{ gap: '16px' }}>
          <div style={{ width: '100%' }}>
            <Form.FieldGroup>
              <Controller
                name="revSplit"
                control={control}
                rules={{ required: 'Revenue split is required' }}
                render={({ field }) => (
                  <RevenueSplitField
                    value={field.value}
                    onChange={(val) => field.onChange(val)}
                    isRequired={!!formState.errors.revSplit}
                    onInfoClick={() => setShowRevenueSplitModal(true)}
                    inputValue={field.value}
                    onInputChange={(val) => field.onChange(val)}
                    inputError={formState.errors.revSplit?.message}
                  />
                )}
              />
            </Form.FieldGroup>
          </div>
          <Divider />
          <Form.FieldGroup>
            <Form.Field
              labelText="Retailer Access"
              id="retailerAccess"
              isRequired
              errorText={formState.errors.retailerAccess?.message || ''}
            >
              <Controller
                name="retailerAccess"
                control={control}
                rules={{ required: 'Please select a retailer access option' }}
                render={({ field: { value, onChange } }) => (
                  <Controller
                    name="approvalThreshold"
                    control={control}
                    rules={{
                      validate: (v) =>
                        value === 'HYBRID' ? !!v || 'Approval threshold required' : true,
                    }}
                    render={({ field: { value: threshold, onChange: onThresholdChange } }) => (
                      <div style={{ width: '100%' }}>
                        <RetailerAccessSlider
                          value={value || 'OPEN'}
                          approvalThreshold={threshold}
                          onSliderChange={(accessValue, newThreshold) => {
                            onChange(accessValue);
                            if (accessValue === 'HYBRID') {
                              onThresholdChange(newThreshold);
                            } else {
                              onThresholdChange(undefined);
                            }
                          }}
                        />
                        {value === 'HYBRID' && (
                          <div style={{ marginTop: 32 }}>
                            <Form.Field
                              labelText="Annual Revenue Auto Approval Threshold"
                              id="approvalThreshold"
                              isRequired
                              errorText={formState.errors.approvalThreshold?.message || ''}
                            >
                              <Input
                                type="text"
                                value={threshold || ''}
                                onChange={(e) => {
                                  // Allow K/M shorthand (e.g., 100K, 1.5M)
                                  let val = e.target.value.toUpperCase().replace(/[^0-9KM.]/g, '');
                                  if (val.endsWith('K')) {
                                    val = String(parseFloat(val) * 1000);
                                  } else if (val.endsWith('M')) {
                                    val = String(parseFloat(val) * 1000000);
                                  }
                                  onThresholdChange(Number(val) || '');
                                }}
                                placeholder="5M"
                                isFullWidth
                              />
                            </Form.Field>
                          </div>
                        )}
                      </div>
                    )}
                  />
                )}
              />
            </Form.Field>
            <Divider />
            <Form.Field
              labelText={
                (
                  <>
                    Fulfillment Times
                    <InfoIconButton
                      type="button"
                      onClick={() => setShowFulfillmentTimesModal(true)}
                      aria-label="Learn more about fulfillment times"
                    >
                      <Icon name="info" size="16px" color="bodyTextCaption" />
                    </InfoIconButton>
                  </>
                ) as any
              }
              id="fulfillmentTimes"
              isRequired
              errorText={formState.errors.fulfillmentTimes?.message}
            >
              <Controller
                name="fulfillmentTimes"
                control={control}
                rules={{ required: 'Please select your fulfillment time' }}
                render={({ field }) => (
                  <Select
                    options={FULFILLMENT_TIME_OPTIONS}
                    value={
                      FULFILLMENT_TIME_OPTIONS.find((option) => option.value === field.value) ||
                      null
                    }
                    onChange={(option) => field.onChange(option?.value)}
                    placeholder="Select fulfillment time"
                    isFullWidth
                  />
                )}
              />
            </Form.Field>
            <Divider />
            <Form.FieldGroup>
              <RadioButton value="hasPolicy" {...register('returnPolicyOption')}>
                Our return policy can be found here:
                <InfoIconButton
                  type="button"
                  onClick={() => setShowReturnPolicyModal(true)}
                  aria-label="Learn more about return policy"
                  style={{ marginLeft: 8 }}
                >
                  <Icon name="info" size="16px" color="bodyTextCaption" />
                </InfoIconButton>
              </RadioButton>
              {selectedOption === 'hasPolicy' && (
                <Form.Field labelText="Return Policy URL" id="returnPolicyURL">
                  <Input
                    type="text"
                    required
                    placeholder="https://www.example.com/return-policy"
                    {...register('returnPolicyURL')}
                    isFullWidth
                  />
                </Form.Field>
              )}
              <RadioButton value="noPolicy" {...register('returnPolicyOption')}>
                We do not accept returns
              </RadioButton>
              {selectedOption === 'noPolicy' && (
                <StatusIcon variant="warning">
                  Having a return policy is recommended for a successful marketplace presence. It
                  helps build trust with retailers and makes them more likely to partner with you.
                </StatusIcon>
              )}
            </Form.FieldGroup>
          </Form.FieldGroup>
          <Form.Footer justifyEnd>
            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </PrimaryButton>
          </Form.Footer>
        </Form>
      </SettingsMain>

      {showRevenueSplitModal && (
        <CustomModalBackdrop onClick={() => setShowRevenueSplitModal(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <CustomModalClose onClick={() => setShowRevenueSplitModal(false)} aria-label="Close">
              ×
            </CustomModalClose>
            <RevenueSplitPanel
              revSplit={watch('revSplit')}
              iconSrc={revSplitIcon}
              iconAlt="Revenue Split"
            />
          </CustomModalContent>
        </CustomModalBackdrop>
      )}
      {showRetailerAccessModal && (
        <CustomModalBackdrop onClick={() => setShowRetailerAccessModal(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <CustomModalClose onClick={() => setShowRetailerAccessModal(false)} aria-label="Close">
              ×
            </CustomModalClose>
            <RetailerAccessPanel iconSrc={lockCheckIcon} iconAlt="Retailer Access" />
          </CustomModalContent>
        </CustomModalBackdrop>
      )}
      {showFulfillmentTimesModal && (
        <CustomModalBackdrop onClick={() => setShowFulfillmentTimesModal(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <CustomModalClose
              onClick={() => setShowFulfillmentTimesModal(false)}
              aria-label="Close"
            >
              ×
            </CustomModalClose>
            <FulfillmentTimesPanel iconSrc={deliveryTruckIcon} iconAlt="Fulfillment Times" />
          </CustomModalContent>
        </CustomModalBackdrop>
      )}
      {showReturnPolicyModal && (
        <CustomModalBackdrop onClick={() => setShowReturnPolicyModal(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <CustomModalClose onClick={() => setShowReturnPolicyModal(false)} aria-label="Close">
              ×
            </CustomModalClose>
            <ReturnPolicyPanel iconSrc={returnPolicyIcon} iconAlt="Return Policy" />
          </CustomModalContent>
        </CustomModalBackdrop>
      )}
    </>
  );
};

export default ServiceOptions;
